import { useEffect, useMemo, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { num } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import { ragTone } from '@/components/ui/statusTone';
import {
  CheckboxInput, NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import type { PartStock } from '../Parts';

/**
 * The catalogue record only. Quantity on hand is deliberately absent — it is
 * the sum of the stock ledger, and letting anyone type over it would break
 * the one guarantee the ledger gives.
 */
export function PartDrawer({
  record, stock, onClose, locked,
}: { record: Record<string, unknown>; stock: PartStock[]; onClose: () => void; locked?: string | null }) {
  const isNew = !record.id;
  const suppliers = useRows<Record<string, unknown>>('suppliers', 'name');

  const [v, setV] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setV({
      uom: 'ea',
      min_level: 0,
      is_critical: false,
      is_wear_part: false,
      is_active: true,
      ...record,
    });
  }, [record]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const position = useMemo(
    () => stock.find((s) => s.part_id === record.id),
    [stock, record.id],
  );

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!String(v.part_no ?? '').trim()) e.part_no = 'Part number is required';
    if (!String(v.description ?? '').trim()) e.description = 'Description is required';
    if (v.max_level != null && v.min_level != null
      && Number(v.max_level) < Number(v.min_level)) {
      e.max_level = 'Maximum is below the minimum';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload = { ...v };
    if (record.id) payload.id = record.id;
    const ok = await save(
      () => upsert('parts', payload as Record<string, unknown> & { id?: string }),
      'Part saved',
    );
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Drawer
      open
      locked={locked}
      width={560}
      title={isNew ? 'New part' : String(v.part_no ?? 'Part')}
      subtitle={isNew ? undefined : String(v.description ?? '')}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this part?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('parts', record.id as string), 'Removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      }
    >
      {position && (
        <div className="issue issue--ok" style={{ marginBottom: 'var(--sp-3)', alignItems: 'center' }}>
          <StatusChip tone={ragTone(position.status_colour)}>
            {num(position.qty_on_hand, 0)} on hand
          </StatusChip>
          {position.qty_on_order > 0 && (
            <span className="small">{num(position.qty_on_order, 0)} on order</span>
          )}
          <span className="spacer" />
          <span className="small">Value {num(position.stock_value, 2)}</span>
        </div>
      )}

      <div className="form-grid">
        <TextInput id="pt-no" label="Part number" required error={errors.part_no}
          value={String(v.part_no ?? '')} onChange={(x) => set('part_no', x)} />
        <TextInput id="pt-cat" label="Category" value={String(v.category ?? '')}
          onChange={(x) => set('category', x || null)}
          help="Filter, liner, hydraulic, electrical…" />

        <div className="field--full">
          <TextInput id="pt-desc" label="Description" required error={errors.description}
            value={String(v.description ?? '')} onChange={(x) => set('description', x)} />
        </div>

        <SelectInput id="pt-sup" label="Supplier" value={(v.supplier_id as string) ?? null}
          options={(suppliers ?? []).map((s) => ({ value: String(s.id), label: String(s.name) }))}
          onChange={(x) => set('supplier_id', x)} />
        <TextInput id="pt-mpn" label="Manufacturer part no"
          value={String(v.manufacturer_part_no ?? '')}
          onChange={(x) => set('manufacturer_part_no', x || null)} />

        <TextInput id="pt-uom" label="Unit of measure" value={String(v.uom ?? 'ea')}
          onChange={(x) => set('uom', x)} />
        <NumberInput id="pt-cost" label="Unit cost" min={0} step={10}
          value={(v.unit_cost as number) ?? null} onChange={(x) => set('unit_cost', x)} />

        <NumberInput id="pt-min" label="Minimum level" min={0} step={1}
          value={(v.min_level as number) ?? 0} onChange={(x) => set('min_level', x ?? 0)}
          help="Below this the part shows amber on the stock list." />
        <NumberInput id="pt-max" label="Maximum level" min={0} step={1} error={errors.max_level}
          value={(v.max_level as number) ?? null} onChange={(x) => set('max_level', x)} />

        <NumberInput id="pt-reorder" label="Reorder quantity" min={0} step={1}
          value={(v.reorder_qty as number) ?? null} onChange={(x) => set('reorder_qty', x)} />
        <NumberInput id="pt-lead" label="Lead time" suffix="days" min={0} step={1}
          value={(v.lead_time_days as number) ?? null} onChange={(x) => set('lead_time_days', x)} />

        <TextInput id="pt-bin" label="Bin location" value={String(v.bin_location ?? '')}
          onChange={(x) => set('bin_location', x || null)} />

        <div className="field--full">
          <CheckboxInput id="pt-crit" label="Critical — a shortage stops a machine"
            value={Boolean(v.is_critical)} onChange={(x) => set('is_critical', x)} />
          <CheckboxInput id="pt-wear" label="Wear part — tracked for life against a component"
            value={Boolean(v.is_wear_part)} onChange={(x) => set('is_wear_part', x)} />
          <CheckboxInput id="pt-active" label="Active"
            value={v.is_active !== false} onChange={(x) => set('is_active', x)} />
        </div>

        <div className="field--full">
          <TextArea id="pt-notes" label="Notes" value={String(v.notes ?? '')}
            onChange={(x) => set('notes', x || null)} />
        </div>
      </div>
    </Drawer>
  );
}
